import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Upload, Plus, Loader2, ImageIcon, Check, Save, IndianRupee } from "lucide-react";
import { FILE_TYPES, fileTypeAccept } from "@/lib/fileTypes";

const BLANK = {
  title: "", title_hi: "", slug: "",
  length: 30, width: 40, floors: 1, facing: "N",
  bhk: 2, area: 1200,
  state: "", category: "", description: "", is_featured: false,
  est_cost_min: "", est_cost_max: "",
  bedrooms: "", living_rooms: "", bathrooms: "", kitchens: "",
};

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const imgSrc = (src) => src?.startsWith("/api/") ? `${BACKEND}${src}` : src;

// Wraps <img> with a one-shot cache-busting retry. Fixes a transient
// "broken image" right after admin upload: the backend PUT to R2 is done,
// but the Cloudflare CDN edge serving the image domain may 404 briefly,
// and the browser caches the failure. On error, retry once after 800 ms
// with a ?t= query so the cached 404 is bypassed.
function RetryImg({ src, alt, className }) {
  const [current, setCurrent] = useState(src);
  useEffect(() => { setCurrent(src); }, [src]);
  const handleError = () => {
    if (!current?.includes("?t=")) {
      setTimeout(() => setCurrent(`${src}${src.includes("?") ? "&" : "?"}t=${Date.now()}`), 800);
    }
  };
  return <img src={current} alt={alt} className={className} onError={handleError} />;
}

const slugify = (s) =>
  s.toLowerCase().replace(/[\s_]+/g, "-").replace(/[^a-z0-9-]/g, "").replace(/-+/g, "-").replace(/^-|-$/g, "");

export default function AddEditPlanPage() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const isEdit = !!planId;

  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [copying, setCopying] = useState(false);
  const [savedPlan, setSavedPlan] = useState(null);
  const [thumbAlt, setThumbAlt] = useState("");
  const [thumbAltSaving, setThumbAltSaving] = useState(false);
  const [imgAlts, setImgAlts] = useState([]);
  const [imgAltSaving, setImgAltSaving] = useState({});
  const [newImgAlt, setNewImgAlt] = useState("");
  const [thumbUploading, setThumbUploading] = useState(false);
  const [imgUploading, setImgUploading] = useState(false);
  const [imgReplacing, setImgReplacing] = useState({}); // { index: bool }
  const [pricesRupees, setPricesRupees] = useState({}); // { file_type: "299" }
  const [priceSaving, setPriceSaving] = useState({});
  const [fileDeleting, setFileDeleting] = useState({}); // { file_type: bool }
  const [publishing, setPublishing] = useState(false);

  // Sync pricesRupees from savedPlan whenever it refreshes
  useEffect(() => {
    if (!savedPlan) return;
    const next = {};
    for (const ft of FILE_TYPES) {
      const f = (savedPlan.files || []).find(x => x.file_type === ft.key);
      next[ft.key] = f ? String(Math.round((f.price_paise || 0) / 100)) : "";
    }
    setPricesRupees(next);
  }, [savedPlan]);

  const savePrice = async (fileType) => {
    if (!activePlanId) return;
    const rupees = parseInt(pricesRupees[fileType] || "0", 10);
    if (isNaN(rupees) || rupees < 0) return alert("Enter a valid non-negative price in rupees");
    setPriceSaving(s => ({ ...s, [fileType]: true }));
    try {
      await api.put(`/admin/plans/${activePlanId}/files/${fileType}/price?price_paise=${rupees * 100}`);
      await refreshPlan(activePlanId);
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    } finally {
      setPriceSaving(s => ({ ...s, [fileType]: false }));
    }
  };

  // Deleting a file is permanent — there is no archived state for it, unlike a
  // plan. Check who already has it first so the admin is warned before the
  // fact rather than finding out when a buyer's re-download 404s.
  const deleteFile = async (fileType, label) => {
    if (!activePlanId) return;
    let warning = "";
    try {
      const { data } = await api.get(`/admin/plans/${activePlanId}/files/${fileType}/usage`);
      const parts = [];
      if (data.downloaded_by > 0) {
        parts.push(`${data.downloaded_by} customer${data.downloaded_by === 1 ? " has" : "s have"} downloaded it`);
      }
      if (data.paid_orders > 0) {
        parts.push(`${data.paid_orders} paid order${data.paid_orders === 1 ? "" : "s"} include${data.paid_orders === 1 ? "s" : ""} it`);
      }
      if (parts.length) {
        warning = `\n\nWarning: ${parts.join(" and ")}. Deleting it will break their re-download.`;
      }
    } catch {
      warning = "\n\nCould not check whether anyone has bought this file.";
    }
    if (!window.confirm(`Delete the ${label} for this plan? This cannot be undone.${warning}`)) return;

    setFileDeleting(s => ({ ...s, [fileType]: true }));
    try {
      await api.delete(`/plans/${activePlanId}/files/${fileType}`);
      await refreshPlan(activePlanId);
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    } finally {
      setFileDeleting(s => ({ ...s, [fileType]: false }));
    }
  };

  const publish = async () => {
    if (!activePlanId) return;
    setPublishing(true);
    try {
      await api.post(`/plans/${activePlanId}/restore`);
      await refreshPlan(activePlanId);
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    } finally {
      setPublishing(false);
    }
  };

  const activePlanId = isEdit ? planId : savedPlan?.id;

  const saveThumbAlt = async () => {
    if (!activePlanId) return;
    setThumbAltSaving(true);
    try {
      await api.patch(`/plans/${activePlanId}/thumbnail-alt?alt=${encodeURIComponent(thumbAlt)}`);
      await refreshPlan(activePlanId);
    } catch (err) { alert(err.response?.data?.detail || err.message); }
    finally { setThumbAltSaving(false); }
  };

  const saveImgAlt = async (index) => {
    if (!activePlanId) return;
    setImgAltSaving(s => ({ ...s, [index]: true }));
    try {
      await api.patch(`/plans/${activePlanId}/image-alt/${index}?alt=${encodeURIComponent(imgAlts[index] || "")}`);
      await refreshPlan(activePlanId);
    } catch (err) { alert(err.response?.data?.detail || err.message); }
    finally { setImgAltSaving(s => ({ ...s, [index]: false })); }
  };

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/plans/${planId}`).then(({ data }) => {
      setSavedPlan(data);
      setThumbAlt(data.thumbnail_alt || "");
      setImgAlts((data.images || []).map(img => (typeof img === "object" ? img.alt : "") || ""));
      setForm({
        title: data.title || "",
        title_hi: data.title_hi || "",
        slug: data.slug || "",
        length: data.length, width: data.width,
        floors: data.floors, facing: data.facing,
        bhk: data.bhk, area: data.area,
        state: data.state || "", category: data.category || "",
        description: data.description || "",
        is_featured: !!data.is_featured,
        est_cost_min: data.est_cost_min ?? "",
        est_cost_max: data.est_cost_max ?? "",
        bedrooms: data.bedrooms ?? "",
        living_rooms: data.living_rooms ?? "",
        bathrooms: data.bathrooms ?? "",
        kitchens: data.kitchens ?? "",
      });
    });
  }, [planId, isEdit]);

  const f = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const refreshPlan = useCallback(async (pid) => {
    const { data } = await api.get(`/plans/${pid}`);
    setSavedPlan(data);
    setThumbAlt(data.thumbnail_alt || "");
    const alts = (data.images || []).map(img => (typeof img === "object" ? img.alt : "") || "");
    setImgAlts(alts);
  }, []);

  const save = async () => {
    if (!form.title) return alert("Title (English) is required");
    if (!form.title_hi) return alert("Hindi title is required");
    if (!form.slug) return alert("SEO URL slug is required");
    setSaving(true);
    try {
      const payload = {
        ...form,
        length: parseFloat(form.length), width: parseFloat(form.width),
        floors: parseInt(form.floors), bhk: parseInt(form.bhk),
        area: parseFloat(form.area),
        est_cost_min: form.est_cost_min !== "" ? parseFloat(form.est_cost_min) : null,
        est_cost_max: form.est_cost_max !== "" ? parseFloat(form.est_cost_max) : null,
        bedrooms: form.bedrooms !== "" ? parseInt(form.bedrooms) : null,
        living_rooms: form.living_rooms !== "" ? parseInt(form.living_rooms) : null,
        bathrooms: form.bathrooms !== "" ? parseInt(form.bathrooms) : null,
        kitchens: form.kitchens !== "" ? parseInt(form.kitchens) : null,
      };
      if (isEdit) {
        await api.put(`/plans/${planId}`, payload);
        await refreshPlan(planId);
        alert("Plan updated!");
      } else {
        const { data } = await api.post("/plans", payload);
        navigate(`/admin/plans/${data.id}/edit`, { replace: true });
      }
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  // Copies everything except identity fields. Title, Hindi title, slug and
  // Naksha No stay blank so a duplicate listing can't be published by accident.
  const copyFromLast = async () => {
    setCopying(true);
    try {
      const { data } = await api.get("/admin/plans/last");
      if (!data) return alert("No previous plan to copy from.");
      setForm(prev => ({
        ...prev,
        length: data.length, width: data.width, floors: data.floors,
        facing: data.facing, bhk: data.bhk, area: data.area,
        state: data.state || "", category: data.category || "",
        description: data.description || "",
        est_cost_min: data.est_cost_min ?? "", est_cost_max: data.est_cost_max ?? "",
        bedrooms: data.bedrooms ?? "", living_rooms: data.living_rooms ?? "",
        bathrooms: data.bathrooms ?? "", kitchens: data.kitchens ?? "",
      }));
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    } finally {
      setCopying(false);
    }
  };

  const uploadImage = async (file, isThumbnail, altText) => {
    if (!activePlanId) return alert("Save the plan first to upload images.");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("is_thumbnail", String(isThumbnail));
    fd.append("alt_text", altText || "");
    isThumbnail ? setThumbUploading(true) : setImgUploading(true);
    try {
      await api.post(`/plans/${activePlanId}/images`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshPlan(activePlanId);
      if (!isThumbnail) setNewImgAlt("");
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    } finally {
      isThumbnail ? setThumbUploading(false) : setImgUploading(false);
    }
  };

  const replaceImage = async (file, index) => {
    if (!activePlanId) return;
    const fd = new FormData();
    fd.append("file", file);
    setImgReplacing(s => ({ ...s, [index]: true }));
    try {
      await api.put(`/plans/${activePlanId}/images/${index}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      await refreshPlan(activePlanId);
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    } finally {
      setImgReplacing(s => ({ ...s, [index]: false }));
    }
  };

  const uploadPdf = async (file, fileType) => {
    if (!activePlanId) return;
    const fd = new FormData();
    fd.append("file_type", fileType);
    fd.append("is_premium", "false");
    fd.append("file", file);
    try {
      await api.post(`/plans/${activePlanId}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshPlan(activePlanId);
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    }
  };

  const Label = ({ children }) => (
    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{children}</label>
  );

  return (
    <div className="bg-slate-50 min-h-screen py-8">
      <div className="nc-container" style={{ maxWidth: "1100px" }}>
        <div className="flex items-center gap-3 mb-6">
          <Link to="/admin?tab=plans" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-emerald-700">
            <ArrowLeft className="w-4 h-4" /> Back to Plans
          </Link>
        </div>

        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>
            {isEdit ? "Edit Plan" : "Add New Plan"}
          </h1>
          {!isEdit && (
            <button type="button" onClick={copyFromLast} disabled={copying}
                    className="nc-btn-secondary text-sm py-2">
              {copying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Copy from last plan
            </button>
          )}
        </div>

        {/* A duplicated plan is created archived so a half-edited "Copy of ..."
            listing is never publicly visible. Offer Publish here so the admin
            doesn't have to go back and find it under "Show archived". */}
        {savedPlan?.is_deleted && (
          <div className="mb-6 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm text-amber-800">
              This plan is <strong>archived</strong> — customers can't see it. Publish it when you're done editing.
            </p>
            <button type="button" onClick={publish} disabled={publishing}
                    className="nc-btn-primary text-sm py-2">
              {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Publish
            </button>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* ── Left: main form ── */}
          <div className="lg:col-span-2 space-y-5">

            {/* Basic info */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
              <h2 className="font-semibold text-slate-900">Plan Details</h2>
              <div>
                <Label>Title (English) *</Label>
                <input
                  className="nc-input mt-1"
                  value={form.title}
                  onChange={e => {
                    const title = e.target.value;
                    setForm(prev => ({
                      ...prev,
                      title,
                      slug: prev.slug && prev.slug !== slugify(prev.title) ? prev.slug : slugify(title),
                    }));
                  }}
                  placeholder="e.g. 30×40 North Facing 2BHK"
                />
              </div>
              <div>
                <Label>शीर्षक (हिंदी) *</Label>
                <input className="nc-input mt-1" value={form.title_hi} onChange={f("title_hi")} placeholder="हिंदी में शीर्षक" />
              </div>
              <div>
                <Label>SEO URL Slug *</Label>
                <input
                  className="nc-input mt-1 font-mono text-sm"
                  value={form.slug}
                  onChange={e => setForm(prev => ({ ...prev, slug: slugify(e.target.value) }))}
                  placeholder="30x40-north-facing-2bhk-vastu"
                />
                <p className="text-xs text-slate-400 mt-1">URL: /plans/<span className="font-medium text-slate-600">{form.slug || "slug-here"}</span></p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Naksha No</label>
                <input className="nc-input mt-1 bg-slate-50 text-slate-500" disabled
                       value={savedPlan?.naksha_no || "Assigned on save"} readOnly />
                <p className="text-xs text-slate-400 mt-1">Auto-assigned. Cannot be edited.</p>
              </div>
              <div>
                <Label>Description</Label>
                <textarea className="nc-input mt-1" rows={4} value={form.description} onChange={f("description")} placeholder="Describe this plan layout, features, highlights..." />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!form.is_featured} onChange={e => setForm({ ...form, is_featured: e.target.checked })} className="rounded" />
                <span className="text-sm text-slate-700">Feature this plan on home page</span>
              </label>
            </div>

            {/* Plot & layout */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
              <h2 className="font-semibold text-slate-900">Plot & Layout</h2>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Length (ft)</Label><input type="number" className="nc-input mt-1" value={form.length} onChange={f("length")} /></div>
                <div><Label>Width (ft)</Label><input type="number" className="nc-input mt-1" value={form.width} onChange={f("width")} /></div>
                <div>
                  <Label>Floors</Label>
                  <select className="nc-input mt-1" value={form.floors} onChange={f("floors")}>
                    {[1, 2, 3, 4].map(n => <option key={n} value={n}>{n} Floor{n > 1 ? "s" : ""}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Road Facing</Label>
                  <select className="nc-input mt-1" value={form.facing} onChange={f("facing")}>
                    {["N","S","E","W","NE","NW","SE","SW"].map(d => <option key={d} value={d}>{d}</option>)}
                    <option value="ANY">Any (suits all orientations)</option>
                  </select>
                </div>
                <div><Label>BHK</Label><input type="number" className="nc-input mt-1" value={form.bhk} onChange={f("bhk")} /></div>
                <div><Label>Built-up Area (sqft)</Label><input type="number" className="nc-input mt-1" value={form.area} onChange={f("area")} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>State</Label>
                  <input className="nc-input mt-1" value={form.state} onChange={f("state")} placeholder="Maharashtra" />
                </div>
                <div>
                  <Label>Category</Label>
                  <select className="nc-input mt-1" value={form.category} onChange={f("category")}>
                    <option value="">Select category</option>
                    <option value="budget">Budget</option>
                    <option value="duplex">Duplex</option>
                    <option value="luxury">Luxury</option>
                    <option value="vastu">Vastu</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Room counts */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
              <h2 className="font-semibold text-slate-900">Room Counts</h2>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Bedrooms</Label><input type="number" className="nc-input mt-1" value={form.bedrooms} onChange={f("bedrooms")} /></div>
                <div><Label>Living Rooms</Label><input type="number" className="nc-input mt-1" value={form.living_rooms} onChange={f("living_rooms")} /></div>
                <div><Label>Bathrooms</Label><input type="number" className="nc-input mt-1" value={form.bathrooms} onChange={f("bathrooms")} /></div>
                <div><Label>Kitchens</Label><input type="number" className="nc-input mt-1" value={form.kitchens} onChange={f("kitchens")} /></div>
              </div>
            </div>

            {/* Estimated cost */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
              <h2 className="font-semibold text-slate-900">Estimated Cost of Construction</h2>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Minimum (₹ Lakhs)</Label>
                  <input type="number" className="nc-input mt-1" value={form.est_cost_min} onChange={f("est_cost_min")} placeholder="e.g. 34" />
                </div>
                <div>
                  <Label>Maximum (₹ Lakhs)</Label>
                  <input type="number" className="nc-input mt-1" value={form.est_cost_max} onChange={f("est_cost_max")} placeholder="e.g. 42" />
                </div>
              </div>
            </div>

            <button onClick={save} disabled={saving} className="nc-btn-primary w-full justify-center py-3 text-base">
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Plan & Upload Images"}
            </button>
          </div>

          {/* ── Right: image uploads ── */}
          <div className="space-y-4">
            {!activePlanId ? (
              <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-8 text-center text-slate-400">
                <ImageIcon className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm font-medium">Images & files</p>
                <p className="text-xs mt-1">Fill in the plan details and click "Create Plan" to unlock image uploads.</p>
              </div>
            ) : (
              <>
                {/* Thumbnail */}
                <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
                  <h3 className="font-semibold text-slate-900">Thumbnail Image</h3>
                  {savedPlan?.thumbnail ? (
                    <RetryImg src={imgSrc(savedPlan.thumbnail)} alt={thumbAlt} className="w-full aspect-video object-cover rounded-xl border border-slate-100" />
                  ) : (
                    <div className="w-full aspect-video rounded-xl bg-slate-100 flex items-center justify-center text-slate-400 text-sm">No thumbnail yet</div>
                  )}
                  <div>
                    <Label>Image Name / Alt Text (SEO)</Label>
                    <input className="nc-input mt-1 text-sm" value={thumbAlt} onChange={e => setThumbAlt(e.target.value)} placeholder="e.g. 30x40 north facing vastu floor plan" />
                    <p className="text-xs text-slate-400 mt-1">Used in img alt tag — helps Google index your image.</p>
                  </div>
                  <div className="flex gap-2">
                    {savedPlan?.thumbnail && (
                      <button onClick={saveThumbAlt} disabled={thumbAltSaving} className="nc-btn-secondary flex-1 justify-center text-sm py-2">
                        {thumbAltSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Alt
                      </button>
                    )}
                    <label className={`nc-btn-secondary flex-1 justify-center text-sm py-2 cursor-pointer ${thumbUploading ? "opacity-60 pointer-events-none" : ""}`}>
                      {thumbUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      {savedPlan?.thumbnail ? "Replace" : "Upload"}
                      <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && uploadImage(e.target.files[0], true, thumbAlt)} />
                    </label>
                  </div>
                </div>

                {/* Additional images — each with its own editable alt */}
                <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
                  <h3 className="font-semibold text-slate-900">Additional Images</h3>

                  {(savedPlan?.images || []).map((img, i) => {
                    const url = typeof img === "object" ? img.url : img;
                    return (
                      <div key={i} className="border border-slate-100 rounded-xl overflow-hidden bg-slate-50">
                        <RetryImg src={imgSrc(url)} alt={imgAlts[i] || ""} className="w-full aspect-video object-cover" />
                        <div className="p-3 space-y-2">
                          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Alt Text (SEO)</label>
                          <input
                            className="nc-input text-sm"
                            value={imgAlts[i] ?? ""}
                            onChange={e => {
                              const next = [...imgAlts];
                              next[i] = e.target.value;
                              setImgAlts(next);
                            }}
                            placeholder="e.g. ground floor plan layout"
                          />
                          <button
                            onClick={() => saveImgAlt(i)}
                            disabled={!!imgAltSaving[i]}
                            className="nc-btn-secondary w-full justify-center text-sm py-2"
                          >
                            {imgAltSaving[i] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                            Save Alt Text
                          </button>
                          <label className="nc-btn-secondary text-xs py-1.5 cursor-pointer inline-flex items-center gap-1.5">
                            {imgReplacing[i]
                              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Replacing…</>
                              : <><Upload className="w-3.5 h-3.5" /> Replace</>}
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp,image/gif"
                              className="hidden"
                              disabled={!!imgReplacing[i]}
                              onChange={e => {
                                const file = e.target.files?.[0];
                                if (file) replaceImage(file, i);
                                e.target.value = "";
                              }}
                            />
                          </label>
                        </div>
                      </div>
                    );
                  })}

                  {/* Add new image */}
                  <div className="border-t border-slate-200 pt-3 space-y-2">
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">New Image Alt Text (SEO)</label>
                    <input className="nc-input text-sm" value={newImgAlt} onChange={e => setNewImgAlt(e.target.value)} placeholder="e.g. master bedroom furniture layout" />
                    <label className={`nc-btn-secondary w-full justify-center text-sm py-2 cursor-pointer ${imgUploading ? "opacity-60 pointer-events-none" : ""}`}>
                      {imgUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      Add Image
                      <input type="file" accept="image/*" className="hidden" onChange={e => { if (e.target.files?.[0]) { uploadImage(e.target.files[0], false, newImgAlt); e.target.value = ""; } }} />
                    </label>
                  </div>
                </div>

                {/* PDF bundles + per-PDF price */}
                <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
                  <h3 className="font-semibold text-slate-900">PDF Bundles & Prices</h3>
                  <p className="text-xs text-slate-500 -mt-1">Set the listing price for each PDF (₹ — GST 18% added at checkout).</p>
                  {FILE_TYPES.map(ft => {
                    const present = savedPlan?.files?.some(f => f.file_type === ft.key);
                    const saving = !!priceSaving[ft.key];
                    return (
                      <div key={ft.key} className={`p-3 rounded-xl border ${present ? "border-emerald-300 bg-emerald-50/50" : "border-slate-200"}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className={`text-sm font-medium flex items-center gap-2 ${present ? "text-emerald-800" : "text-slate-700"}`}>
                            {present ? <Check className="w-4 h-4" /> : <Upload className="w-4 h-4 text-slate-400" />}
                            {ft.label}
                          </span>
                          <div className="flex items-center gap-3">
                            <label className="text-xs text-slate-500 hover:text-emerald-700 cursor-pointer">
                              {present ? `Replace ${ft.key === "cad" ? "file" : "PDF"}` : `Upload ${ft.key === "cad" ? "file" : "PDF"}`}
                              <input type="file" accept={fileTypeAccept(ft.key)} className="hidden" onChange={e => e.target.files?.[0] && uploadPdf(e.target.files[0], ft.key)} />
                            </label>
                            {present && (
                              <button
                                type="button"
                                onClick={() => deleteFile(ft.key, ft.label)}
                                disabled={!!fileDeleting[ft.key]}
                                className="text-xs text-rose-600 hover:text-rose-700 disabled:opacity-50"
                                data-testid={`delete-file-${ft.key}`}
                              >
                                {fileDeleting[ft.key] ? "Deleting…" : "Delete"}
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="mt-2 flex items-center gap-2">
                          <span className="inline-flex items-center text-xs text-slate-500"><IndianRupee className="w-3.5 h-3.5" /></span>
                          <input
                            type="number" min="0" step="1"
                            className="nc-input text-sm flex-1"
                            placeholder={`Default ₹${ft.defaultPaise / 100}`}
                            value={pricesRupees[ft.key] ?? ""}
                            onChange={e => setPricesRupees(p => ({ ...p, [ft.key]: e.target.value }))}
                            disabled={!present}
                            data-testid={`price-input-${ft.key}`}
                          />
                          <button
                            onClick={() => savePrice(ft.key)}
                            disabled={!present || saving}
                            className="nc-btn-secondary text-xs py-1.5 px-2.5"
                            data-testid={`save-price-${ft.key}`}
                          >
                            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
                          </button>
                        </div>
                        {!present && (
                          <p className="text-[11px] text-slate-400 mt-1">
                            Upload the {ft.key === "cad" ? "file" : "PDF"} first to set its price.
                          </p>
                        )}
                        {ft.key === "cad" && (
                          <p className="text-xs text-amber-600 mt-1">
                            Not watermarked — CAD files are editable masters. The buyer's Naksha No and
                            mobile number are stamped into the download filename for traceability.
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
